import { z } from 'zod'
import dayjs, { type Dayjs } from 'dayjs'
import type { TDictionaryItem } from '../../../../api/types'
import {
  DEFAULT_USER_STATUS,
  USER_STATUS_VALUES,
  isUserStatusValue,
  type UserStatusValue,
} from '../../../../consts/userStatus'
import { FIELD_CANNOT_BE_EMPTY } from '../../../../consts/validationErrors'
import type { UserDescription } from '../../../../types/entitys/users'
import {
  fullYearsSinceBirthday,
  parseIsoBirthdayToDayjs,
} from '../../../../utils/birthdayFromAge'
import {
  buildIdArrayFromProfileNames,
  dictionaryIdArraySchema,
} from '../../../../utils/dictionary/formHelpers'

const isDayjsOrNull = (val: unknown): val is Dayjs | null =>
  val === null || dayjs.isDayjs(val)

const USER_SLUG_RESERVED = new Set([
  'me',
  'stats',
  'restore',
  'avatar',
  'follows',
  'followers',
  'settings',
  'search',
  'admin',
])

const normalizeUserSlug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')

/** Placeholder `user-{id}` допускается как текущее значение; смена на чужой/новый — на бэке. */
const userSlugSchema = z
  .string({ required_error: 'Укажите имя профиля' })
  .transform(normalizeUserSlug)
  .pipe(
    z
      .string()
      .min(3, { message: 'Имя профиля — минимум 3 символа' })
      .max(40, { message: 'Имя профиля — максимум 40 символов' })
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
        message: 'Имя профиля: только латиница, цифры и дефис (например ivan-ivanov)',
      })
      .refine((value) => !USER_SLUG_RESERVED.has(value), {
        message: 'Это имя профиля занято системой',
      })
  )

export const userFormSchema = z.object({
  surname: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .max(120, { message: 'Максимально 120 символов' }),
  name: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .max(120, { message: 'Максимально 120 символов' }),
  otch: z
    .string()
    .trim()
    .max(120, { message: 'Максимально 120 символов' }),
  slug: userSlugSchema,
  status: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .pipe(
      z.enum(USER_STATUS_VALUES, {
        message: FIELD_CANNOT_BE_EMPTY,
      })
    ),
  birthday: z
    .custom<Dayjs | null>(isDayjsOrNull, {
      message: 'Укажите дату рождения',
    })
    .refine((val) => val !== null, { message: 'Укажите дату рождения' })
    .refine((val) => val !== null && val.isValid(), { message: 'Некорректная дата' })
    .refine(
      (val) => {
        if (val === null) return false
        const years = fullYearsSinceBirthday(val)
        return years >= 14 && years <= 100
      },
      { message: 'Возраст должен быть от 14 до 100 лет' }
    )
    .transform((val) => val as Dayjs),
  slogan: z
    .string()
    .trim()
    .max(255, { message: 'Максимально 255 символов' }),
  region: z.string().trim(),
  city: z.string().trim(),
  work: z
    .string()
    .trim()
    .max(500, { message: 'Максимально 500 символов' }),
  description: z
    .string()
    .trim()
    .max(500, { message: 'Максимально 500 символов' }),
  full_description: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .max(65535, { message: 'Текст слишком длинный' }),
  image: z
    .union([z.string(), z.instanceof(File), z.null()])
    .refine(
      (val) =>
        val instanceof File || (typeof val === 'string' && val.trim().length > 0),
      { message: 'Загрузите аватар' },
    ),
  competencyIds: dictionaryIdArraySchema('Добавьте хотя бы одну компетенцию из справочника'),
  resourceIds: dictionaryIdArraySchema('Добавьте хотя бы один ресурс из справочника'),
  participationIds: dictionaryIdArraySchema('Добавьте хотя бы одно условие участия из справочника'),
  improvementIds: dictionaryIdArraySchema('Добавьте хотя бы одно направление развития из справочника'),
  matchingRemoteAllowed: z.boolean(),
})

/** Значения полей до парсинга (совпадает с тем, что держит react-hook-form). */
export type UserFormValues = z.input<typeof userFormSchema>
/** Результат валидации Zod (после transform у status и т.д.). */
export type UserFormOutput = z.output<typeof userFormSchema>

export type ProfileFormDictionaries = {
  competencies: TDictionaryItem[]
  resources: TDictionaryItem[]
  participations: TDictionaryItem[]
  improvements: TDictionaryItem[]
}

export const userToFormValues = (
  user: UserDescription,
  dictionaries?: ProfileFormDictionaries
): UserFormValues => {
  const base = {
    surname: user.surname,
    name: user.name,
    otch: user.otch,
    slug: user.slug ?? '',
    status: isUserStatusValue(user.status) ? user.status : DEFAULT_USER_STATUS,
    birthday: parseIsoBirthdayToDayjs(user.birthday),
    slogan: user.slogan ?? '',
    region: user.region ?? '',
    city: user.city ?? '',
    work: user.work ?? '',
    description: user.description ?? '',
    full_description: user.full_description ?? '',
    image: user.image ?? '',
    matchingRemoteAllowed: user.matchingRemoteAllowed,
  }

  if (!dictionaries) {
    return {
      ...base,
      competencyIds: [],
      resourceIds: [],
      participationIds: [],
      improvementIds: [],
    }
  }

  return {
    ...base,
    competencyIds: buildIdArrayFromProfileNames(user.competencies, dictionaries.competencies),
    resourceIds: buildIdArrayFromProfileNames(user.availableResources, dictionaries.resources),
    participationIds: buildIdArrayFromProfileNames(
      user.conditionsOfParticipation,
      dictionaries.participations
    ),
    improvementIds: buildIdArrayFromProfileNames(
      user.directionsOfDevelopment,
      dictionaries.improvements
    ),
  }
}

const dedupeIdsPreserveOrder = (ids: number[]): number[] => {
  const seen = new Set<number>()
  const out: number[] = []
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i]
    if (id > 0 && !seen.has(id)) {
      seen.add(id)
      out.push(id)
    }
  }
  return out
}

export type UpdateUserPayload = {
  surname: string
  name: string
  otch: string
  slug: string
  status: UserStatusValue
  /** YYYY-MM-DD для API (из поля «дата рождения»), либо null */
  birthday: string | null
  slogan: string
  region: string
  city: string
  work: string
  description: string
  full_description: string
  image: string
  competencies: number[]
  availableResources: number[]
  conditionsOfParticipation: number[]
  directionsOfDevelopment: number[]
  matchingRemoteAllowed: boolean
}

export const userFormToPayload = (data: UserFormOutput): UpdateUserPayload => ({
  surname: data.surname.trim(),
  name: data.name.trim(),
  otch: data.otch.trim(),
  slug: data.slug,
  status: data.status,
  birthday: data.birthday.isValid() ? data.birthday.format('YYYY-MM-DD') : null,
  slogan: data.slogan.trim(),
  region: data.region.trim(),
  city: data.city.trim(),
  work: data.work.trim(),
  description: data.description.trim(),
  full_description: data.full_description.trim(),
  image: typeof data.image === 'string' ? data.image : '',
  competencies: dedupeIdsPreserveOrder(data.competencyIds),
  availableResources: dedupeIdsPreserveOrder(data.resourceIds),
  conditionsOfParticipation: dedupeIdsPreserveOrder(data.participationIds),
  directionsOfDevelopment: dedupeIdsPreserveOrder(data.improvementIds),
  matchingRemoteAllowed: data.matchingRemoteAllowed,
})
